import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import moment from "moment";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step2 from "@/components/menu-management/combo-meal/add-combo-meal/step2";
import Step1 from "@/components/menu-management/combo-meal/add-combo-meal/step1";
import Step3 from "@/components/menu-management/combo-meal/add-combo-meal/step3";
import { useFetchAllMenuData } from "@/hooks/use-fetch-all-menu-data";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewComboMeal,
  clearComboMealMessages,
  updateExistingComboMeal,
} from "@/redux/slice/menu-manage-slice/combo-meal-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchAllDays } from "@/redux/slice/utility/day-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const combo_meal_id = 9;

function CreateComboMeal() {
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [dayTimes, setDayTimes] = useState([]);
  const item = location.state;

  const fetchOnly = useMemo(() => ["categories"], []);

  const { groupLoading } = useFetchAllMenuData(
    user.restaurant_id,
    combo_meal_id,
    {
      fetchOnly,
    }
  );

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.comboMeal
  );
  const { getLoading } = useSelector((state) => state.menuManage.menu);

  const form = useForm({
    resolver: zodResolver(createComboMealschema),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: "",
      name: "",
      description: "",
      category_id: null,
      combo_days: [],
      combo_items: [],
      price: null,
      discount_price: null,
      is_day_time_offer: true,
      vat: 0,
      status: "active",
      file_manager_id: undefined,
    },
  });

  const errorMessage = getFirstErrorMessage(form);

  const { step, nextStep, prevStep, setStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Combo Meal",
      user,
      createAction: createNewComboMeal,
      updateAction: updateExistingComboMeal,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/combo-meal",
    });

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user.restaurant_id));
    dispatch(fetchAllDays());
  }, []);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/combo-meal");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      category_id: item.category_id || null,
      combo_days: item.combo_days?.map((c) => c.id) || [],
      price: item.price ?? null,
      discount_price: item.discount_price ?? null,
      is_day_time_offer: item.is_day_time_offer ?? true,
      vat: item.vat ?? 0,
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item?.combo_items || []));
    const formattedDays =
      item?.combo_days?.map((day) => ({
        ...day,
        start_time: moment(day.start_time, "HH:mm:ss").format("HH:mm"),
        end_time: moment(day.end_time, "HH:mm:ss").format("HH:mm"),
      })) || [];

    setDayTimes(formattedDays);
  }, [type, item?.id, form]);

  if (groupLoading || getLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update"
            ? "Update Combo/Set/Offer- Meal"
            : "Add New Combo/Set/Offer- Meal"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Combo/Set/Offer- Meal", path: "/backoffice/menu/combo-meal" },
        {
          name: `${
            type == "update"
              ? "Update Combo/Set/Offer- Meal"
              : "Add New Combo/Set/Offer- Meal"
          }`,
          path: `/backoffice/menu/combo-meal/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 combo_meal_id={combo_meal_id} form={form} />}
            {step === 1 && (
              <Step2
                item_id={entityId || item?.id}
                combo_meal_id={combo_meal_id}
                dayTimes={dayTimes}
                setDayTimes={setDayTimes}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/combo-meal"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateComboMeal;

export const createComboMealschema = z.object({
  name: z.string().min(1, "Name is required"),

  item_no: z
    .preprocess((val) => {
      if (val === "" || val == null) return null;
      return Number(val);
    }, z.number({ invalid_type_error: "Item no must be a number" }).nullable())
    .optional(),

  description: z.string().optional(),

  category_id: z
    .preprocess((val) => {
      if (val === "" || val === undefined || val === null) return null;
      return Number(val);
    }, z.number().nullable())
    .optional(),

  combo_days: z
    .array(
      z.object({
        day_id: z.number().min(1, "Day is required"),
        // start_time: z.string().min(1, "Start time required"),
        // end_time: z.string().min(1, "End time required"),
        is_active: z.boolean().default(true),
      })
    )
    .optional(),

  combo_items: z.array(z.object({}).catchall(z.any())).optional(),

  price: z
    .preprocess(
      (val) =>
        val === null || val === undefined || val === "" ? null : Number(val),
      z.number({ invalid_type_error: "Price must be a number" }).nullable()
    )
    .optional(),

  discount_price: z
    .preprocess(
      (val) =>
        val === null || val === undefined || val === "" ? null : Number(val),
      z
        .number({ invalid_type_error: "Discount price must be a number" })
        .nullable()
    )
    .optional(),

  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z
        .number({
          invalid_type_error: "VAT percentage must be a number",
        })
        .refine((val) => val >= 0, {
          message: "VAT percentage cannot be negative",
        })
        .refine((val) => val <= 100, {
          message: "VAT percentage cannot exceed 100",
        })
    )
    .optional(),

  file_manager_id: z
    .number({
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),

  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
